import { Check, ChevronDown, Filter, X } from "lucide-react";
import React, { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
	FILTER_DIMENSIONS,
	FilterDimension,
	FilterOption,
	ReportFilters,
} from "../../../models/ReportBuilder";
import { NO_SUBSIDIARY } from "../../../models/Subsidiary";
import { useCorporateGroups } from "../../../networking/corporateGroup.hooks";
import { useFilterNames, useFilterOptions } from "../../../networking/report-builder.hooks";
import { useSubsidiaries } from "../../../networking/subsidiary.hooks";
import { useTranslation } from "react-i18next";

interface FilterBarProps {
	filters?: ReportFilters;
	onChange: (filters?: ReportFilters) => void;
	/** Read mode shows the active filters as text only. */
	editable: boolean;
}

/**
 * Koncern and dotterbolag options come from the organisation's own lists (not
 * the lake), which also tells us whether the dimension exists at all.
 * `undefined` while loading.
 */
function useStaticOptions(): Partial<Record<keyof ReportFilters, FilterOption[] | undefined>> {
	const { t } = useTranslation();
	const groups = useCorporateGroups({ page: 1, pageSize: 1000, sort: "name" });
	const subsidiaries = useSubsidiaries();
	return useMemo(
		() => ({
			corporateGroupIds: groups.corporateGroups?.corporateGroups.map((g) => ({
				id: g.id,
				name: g.name,
			})),
			subsidiaryIds: subsidiaries.isSuccess
				? [
						...subsidiaries.subsidiaries.map((s) => ({ id: s.id, name: s.name })),
						{ id: NO_SUBSIDIARY, name: t("NO_SUBSIDIARY") },
				  ]
				: undefined,
		}),
		[groups.corporateGroups, subsidiaries.isSuccess, subsidiaries.subsidiaries, t]
	);
}

/** Options and names for one dimension: the static list when there is one, otherwise lake queries. */
function useDimensionOptions(
	dimension: FilterDimension,
	search: string,
	selected: string[],
	staticOptions: FilterOption[] | undefined,
	open: boolean
) {
	const lake = dimension.source === "lake";
	const lakeOptions = useFilterOptions(dimension.key, search, open && lake);
	const lakeNames = useFilterNames(dimension.key, lake ? selected : []);
	const q = search.trim().toLowerCase();
	const options = lake
		? lakeOptions.data
		: staticOptions?.filter((o) => !q || o.name.toLowerCase().includes(q));
	const nameOf = (id: string) =>
		(lake ? lakeNames.data : staticOptions)?.find((o) => o.id === id)?.name ??
		lakeOptions.data?.find((o) => o.id === id)?.name ??
		id;
	return {
		options,
		isLoading: lake ? lakeOptions.isLoading : staticOptions === undefined,
		error: lake ? lakeOptions.error : undefined,
		nameOf,
	};
}

const DimensionPicker: React.FC<{
	dimension: FilterDimension;
	selected: string[];
	staticOptions?: FilterOption[];
	onChange: (ids: string[]) => void;
}> = ({ dimension, selected, staticOptions, onChange }) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const options = useDimensionOptions(dimension, search, selected, staticOptions, open);
	const { nameOf } = options;
	const toggle = (id: string) =>
		onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
	const active = selected.length > 0;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className={cn(
						"inline-flex h-8 max-w-[16rem] items-center gap-1 rounded-md border px-2 text-xs font-medium",
						active
							? "border-action/40 bg-action/10 text-action"
							: "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
					)}
				>
					<span className="truncate">
						{dimension.label}
						{active
							? `: ${selected.length === 1 ? nameOf(selected[0]) : `${selected.length} valda`}`
							: ""}
					</span>
					<ChevronDown className="h-3 w-3 shrink-0" />
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="z-[1300] w-72 p-2">
				<input
					autoFocus
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder={`Sök ${dimension.labelOne}…`}
					className="mb-2 w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-brand focus:outline-none"
				/>
				{active && (
					<div className="mb-2 flex flex-wrap gap-1">
						{selected.map((id) => (
							<button
								key={id}
								type="button"
								onClick={() => toggle(id)}
								className="inline-flex max-w-full items-center gap-1 rounded-full bg-action/10 px-2 py-0.5 text-[11px] text-action"
							>
								<span className="truncate">{nameOf(id)}</span>
								<X className="h-3 w-3 shrink-0" />
							</button>
						))}
					</div>
				)}
				<div className="max-h-64 overflow-y-auto">
					{options.isLoading && <p className="p-2 text-xs text-gray-500">Hämtar…</p>}
					{options.error && (
						<p className="p-2 text-xs text-red-600">
							{options.error.error?.detail || "Kunde inte hämta alternativ"}
						</p>
					)}
					{options.options?.length === 0 && (
						<p className="p-2 text-xs text-gray-500">Inga träffar.</p>
					)}
					{options.options?.map((o) => {
						const on = selected.includes(o.id);
						return (
							<button
								key={o.id}
								type="button"
								onClick={() => toggle(o.id)}
								className={cn(
									"flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-gray-100",
									on && "font-medium text-gray-900"
								)}
							>
								<span
									className={cn(
										"flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid",
										on ? "border-action bg-action text-white" : "border-gray-300 bg-white"
									)}
								>
									{on && <Check className="h-3 w-3" />}
								</span>
								<span className="truncate">{o.name}</span>
							</button>
						);
					})}
					{dimension.source === "lake" && options.options && options.options.length >= 200 && (
						<p className="p-2 text-[11px] text-gray-400">Visar de första 200; sök för fler.</p>
					)}
				</div>
				{active && (
					<button
						type="button"
						onClick={() => onChange([])}
						className="mt-2 w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
					>
						Rensa {dimension.label.toLowerCase()}
					</button>
				)}
			</PopoverContent>
		</Popover>
	);
};

const ActiveSummary: React.FC<{
	dimension: FilterDimension;
	ids: string[];
	staticOptions?: FilterOption[];
}> = ({ dimension, ids, staticOptions }) => {
	const { nameOf, isLoading } = useDimensionOptions(dimension, "", ids, staticOptions, false);
	const text = ids.map((id) => (isLoading ? "…" : nameOf(id))).join(", ");
	return (
		<span
			className="inline-flex max-w-[20rem] items-center gap-1 rounded-full bg-action/10 px-2 py-0.5 text-xs text-action"
			title={text}
		>
			<span className="font-medium">{dimension.label}:</span>{" "}
			<span className="truncate">{text}</span>
		</span>
	);
};

/**
 * Report-level filters (koncern, pool, dotterbolag, contract), bound into
 * every widget's :filter(...) placeholders. Koncern and dotterbolag only show
 * when the organisation has defined any; legacy customer/consultant filters
 * only while a saved report still carries them. Widgets written before the
 * placeholders existed ignore them, which the card footer says.
 */
const FilterBar: React.FC<FilterBarProps> = ({ filters, onChange, editable }) => {
	const staticOptions = useStaticOptions();
	const hasValues = (d: FilterDimension) => (filters?.[d.key]?.length ?? 0) > 0;
	const active = FILTER_DIMENSIONS.filter(hasValues);
	// Koncern/dotterbolag pickers need at least one defined; the sentinel alone does not count.
	const defined = (d: FilterDimension) =>
		d.source === "lake" ||
		(staticOptions[d.key]?.filter((o) => o.id !== NO_SUBSIDIARY).length ?? 0) > 0;
	const pickers = FILTER_DIMENSIONS.filter((d) => hasValues(d) || (!d.legacy && defined(d)));
	if (!editable && active.length === 0) return null;
	return (
		<div className="flex flex-wrap items-center gap-2">
			<Filter className="h-3.5 w-3.5 text-gray-400" />
			{editable
				? pickers.map((d) => (
						<DimensionPicker
							key={d.key}
							dimension={d}
							selected={filters?.[d.key] ?? []}
							staticOptions={staticOptions[d.key]}
							onChange={(ids) => onChange({ ...(filters ?? {}), [d.key]: ids })}
						/>
				  ))
				: active.map((d) => (
						<ActiveSummary
							key={d.key}
							dimension={d}
							ids={filters?.[d.key] ?? []}
							staticOptions={staticOptions[d.key]}
						/>
				  ))}
			{editable && active.length > 0 && (
				<button
					type="button"
					onClick={() => onChange(undefined)}
					className="text-xs text-gray-500 hover:text-gray-800"
				>
					Rensa alla
				</button>
			)}
		</div>
	);
};

export default FilterBar;
